/**
 * Every outward link this site makes, in one file.
 *
 * The home page's whole job is to send people to the right app, so the set of
 * hostnames below IS the product surface.
 *
 * These are the FINAL hostnames, set by the user on 2026-09-02 and LIVE as of
 * that date — all three records created in Route 53 and verified serving:
 *
 *   Customer Chat   customers.jackdesk.com    200  (plural — see below)
 *   Teams Chat      teams.jackdesk.com        200
 *   Workbook        workbook.jackdesk.com     200
 *   JackSync        drive.jackdesk.com        200  unchanged
 *   Store           store.jackdesk.com        200  unchanged
 *
 * Customer Chat is `customers.` PLURAL. A singular `customer.` was considered
 * (it mirrors the product name, as `teams.` does) and rejected; `customer.`
 * has no DNS record and never will. Do not "correct" it back.
 *
 * `chat.jackdesk.com`, Teams Chat's old home, NO LONGER RESOLVES — so it is
 * not a fallback. `jackdesk.com` still serves the Customer Chat bundle and is
 * byte-identical to customers.jackdesk.com; it frees up for this page once
 * Customer Chat traffic has drained off the apex.
 *
 * webhook.jackdesk.com and productivity.jackdesk.com are LIVE BACKEND VHOSTS,
 * not spare names — repointing either at CloudFront silently kills a running
 * service. That is why Workbook got a third name.
 */

const env = import.meta.env;

/** An app a visitor can be sent to. */
export interface AppLink {
  slug: string;
  /** Product slug in the licensing catalogue, when it is a sellable product. */
  catalogSlug?: string;
  name: string;
  tagline: string;
  /** Where it lives today. */
  href: string;
  /** The hostname it used to answer on, kept for the redirect period. */
  movedFrom?: string;
  /** False for anything a visitor cannot open yet. */
  live: boolean;
  /**
   * Built, not yet open to everyone. Rendered as "Early access" with a button
   * that opens the chat rather than a link: there is no hostname to send
   * anyone to yet (hr. and erp. have no DNS record as of 2026-09-25), and the
   * point of listing it is to start a conversation.
   */
  earlyAccess?: boolean;
  icon: string;
}

export const APPS: AppLink[] = [
  {
    slug: 'customer-chat',
    catalogSlug: 'customer_chat',
    name: 'Omnichannel Messaging',
    tagline: 'One business number for your whole team: WhatsApp, email, Messenger, SMS and web chat, with tickets and campaigns.',
    href: env.VITE_CUSTOMER_CHAT_URL || 'https://customers.jackdesk.com',
    movedFrom: 'jackdesk.com',
    live: true,
    icon: 'chat',
  },
  {
    slug: 'teams-chat',
    catalogSlug: 'teams_chat',
    name: 'Teams Chat',
    tagline: 'Channels, direct messages, calls and meetings for your own staff.',
    href: env.VITE_TEAMS_CHAT_URL || 'https://teams.jackdesk.com',
    movedFrom: 'chat.jackdesk.com',
    live: true,
    icon: 'team',
  },
  {
    slug: 'jacksync',
    catalogSlug: 'data_sync',
    name: 'File Storage (JackSync)',
    tagline: 'Shared folders in step on every computer, with version history and an audit trail.',
    href: 'https://drive.jackdesk.com',
    live: true,
    icon: 'sync',
  },
  {
    slug: 'workbook',
    catalogSlug: 'workbook',
    name: 'Workbook',
    tagline: 'Attendance, timesheets, field sessions and payroll inputs.',
    href: env.VITE_WORKBOOK_URL || 'https://workbook.jackdesk.com',
    live: true,
    icon: 'clock',
  },
  {
    slug: 'hrms',
    name: 'HR Management',
    tagline: 'People, leave, attendance and payroll with PF, ESI, PT and TDS — plus expenses, joining and exit, on phone and web.',
    href: '',
    live: false,
    earlyAccess: true,
    icon: 'users',
  },
  {
    // Live since 2026-10-07 (Caddy on the ERP box serves erp.jackdesk.com).
    slug: 'erp',
    name: 'Books & ERP',
    tagline: 'Accounts, GST invoicing, buying, stock and reports for every organisation, behind each industry pack.',
    href: 'https://erp.jackdesk.com',
    live: true,
    icon: 'ledger',
  },
  {
    slug: 'store',
    name: 'Store & billing',
    tagline: 'Plans, seats, invoices and every app’s settings in one console.',
    href: 'https://store.jackdesk.com',
    live: true,
    icon: 'store',
  },
];

/**
 * The trial offer (owner, 2026-09-25): no free plan — instead the ADVANCED
 * plan free for three months.
 *
 * Kept here, not in the catalogue, because the catalogue has no notion of a
 * trial offer: `Subscription.status` has a `trialing` state, but nothing
 * creates one from a signup. So a trial is granted by a person, and the
 * button that offers it opens the chat. `planSlug` is looked up in the live
 * catalogue for the plan's name and price, so only the length lives here.
 *
 * The catalogue still sells `cc_free` (WhatsApp, 2 agents). The page no
 * longer shows it; take it off sale in the store if the Free plan is gone.
 */
export const TRIAL = { planSlug: 'cc_advanced', months: 3 };

/**
 * WhatsApp message charges. Jackdesk passes Meta's rate straight through — no
 * markup (owner, 2026-09-26) — which is the other half of the price story:
 * other tools add 10-40% to every template they send.
 *
 * Meta's India rate card, per delivered message, excl. GST, effective
 * 1 July 2026 (developers.facebook.com/docs/whatsapp/pricing, INR CSV).
 *
 * From 1 OCTOBER 2026 Meta also charges for service messages and for utility
 * replies inside the 24-hour window, at the utility rate, with 1,000 free
 * service messages per number per month. So nothing on this page may say
 * "replies are free". Update this block when Meta publishes a new card.
 */
export const META_RATES = {
  marketing: 0.8631,
  utility: 0.1150,
  authentication: 0.1150,
  effective: '1 July 2026',
  source: 'https://developers.facebook.com/docs/whatsapp/pricing',
};

/**
 * Where "Sign in" goes from each product's page: that product's own home in its app (owner, 2026-10-21:
 * "each page login should take to the corresponding page's home since the applications already ... redirect
 * to login page if not logged in"). The desk's deep links answer 404 from CloudFront but with the app's own
 * index.html, so the browser opens the right screen (checked 2026-10-21); hr.jackdesk.com has no DNS record,
 * so HR signs in at the desk until it has a host.
 */
const DESK = env.VITE_CUSTOMER_CHAT_URL || 'https://customers.jackdesk.com';
export const SIGN_IN_AT: Record<string, string> = {
  messaging: `${DESK}/chat`,
  crm: `${DESK}/revenue`,
  teams: `${env.VITE_TEAMS_CHAT_URL || 'https://teams.jackdesk.com'}/team`,
  workbook: env.VITE_WORKBOOK_URL || 'https://workbook.jackdesk.com',
  hr: DESK,
  files: 'https://drive.jackdesk.com',
  fleet: `${DESK}/fleet`,
  ca: `${DESK}/practice`,
  retail: `${DESK}/shop`,
  workspace: `${DESK}/workspace`,
  detailing: `${DESK}/detailing`,
  ai: DESK,
};

/** The licensing/admin console. Also where signed-in people manage seats. */
export const STORE_URL = 'https://store.jackdesk.com';

/**
 * Identity lives in the STORE CONSOLE.
 *
 * Every generic call to action on this page — "Start free", "Get started",
 * "Create an organisation", the footer's "Sign in" — lands on
 * store.jackdesk.com, which carries the real SignIn and SignUp views and is
 * where a new customer picks a plan anyway. The links that do NOT come here are
 * app-specific ones: a card that opens Customer Chat or Teams Chat goes to that
 * app's own login.
 *
 * The NAV's "Sign in" is the one deliberate exception: it scrolls to #apps
 * instead of using SIGN_IN_URL. A returning visitor is signing in to a product,
 * not to billing, and the console cannot guess which — so it is shown the app
 * list. Do not "fix" it back to SIGN_IN_URL (changed 2026-09-12).
 *
 * `?signin` / `?signup` open the console straight on that form. Without the
 * parameter the console lands on the plans list, which is a fine destination
 * but is not what a button marked "Start free" promises.
 *
 * Carried over from jackdesk-ui (fixed there 2026-09-02), and still true of
 * the console's own signup:
 *  - "New or returning" is asked ONCE, before the route splits. It used to sit
 *    inside the email form, which the Google and Zoho buttons never reached, so
 *    those always used the default and a new visitor was refused and then
 *    dropped into a registration dialog.
 *  - An account is always an organisation. Every product is org-scoped, so a
 *    personal account can subscribe to nothing — and the catalogue confirms it:
 *    ?audience=individual returns zero products.
 *  - The server decides, not the screen: /auth/login/* refuses an unknown email
 *    and /auth/signup/* refuses a known one. The UI only picks the friendlier
 *    path; a wrong guess is corrected rather than obeyed.
 */
export const IDENTITY_URL = env.VITE_IDENTITY_URL || STORE_URL;
export const SIGN_IN_URL = `${IDENTITY_URL}/?signin`;
export const SIGN_UP_URL = `${IDENTITY_URL}/?signup`;

export const SUPPORT_EMAIL = 'jackdesk.admin@gmail.com';
export const CONTACT_EMAIL = 'kjagasabarivel@gmail.com';
export const CONTACT_PHONE = '+91 88700 92425';
export const CONTACT_PHONE_HREF = 'tel:+918870092425';
export const CONTACT_ADDRESS = ['Kuranguchavdi', 'Salem, Tamil Nadu', 'India - 636002'];
/**
 * The website chat widget.
 *
 * `AUTH_URL/auth/guest` mints a short-lived GUEST token scoped to
 * WEBSITE_OWNING_ORG, which the socket server accepts as `?token=`. Verified
 * live 2026-09-03: the endpoint returns 201 with a JWT carrying
 * `service: ui_chat_widget_client`, and the socket.io handshake answers 200.
 *
 * Events, from the Angular widget this replaces:
 *   emit  website_chatwidget_messages_front_to_back  (a plain string)
 *   on    website_chatwidget_messages_back_to_front  ({ message })
 */
export const AUTH_URL = env.VITE_AUTH_URL || 'https://auth.jackdesk.com';
export const WEBSOCKET_URL = env.VITE_WEBSOCKET_URL || 'https://websocket.jackdesk.com';
/** The org that owns jackdesk.com itself, i.e. who answers this widget. */
export const WEBSITE_OWNING_ORG = 5;

/**
 * Videos on the Jackdesk YouTube channel, by what they show.
 *
 * Played in place from youtube-nocookie.com, and only after a click — the page
 * loads no YouTube code until then. Titles checked against YouTube's oEmbed on
 * 2026-09-25. The old DEMO_VIDEO_URL (Ej40xbrwL-A) was removed that day: the
 * video had been taken down, so "Watch the demo" led to "Video unavailable".
 */
export interface Video { id: string; title: string }

export const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@jackdesk';

export const VIDEOS = {
  service: { id: 'VAQk-0E64tc', title: 'Jackdesk for service centres' },
  school: { id: '7PYb8m-b5Wo', title: 'Jackdesk for schools' },
  realestate: { id: 'LGijAQsTmks', title: 'Jackdesk for real estate' },
  clothing: { id: 'zEXy-zFU-Tc', title: 'Jackdesk for clothing brands' },
  clinic: { id: 'Yg8VywEPs0c', title: 'Jackdesk for hospitals and clinics' },
  automation: { id: '14ndDfxz0T4', title: 'Building a document-collection chatbot with Jackdesk' },
} satisfies Record<string, Video>;

/**
 * Installers, published to S3.
 *
 * Object names are VERSIONED on purpose: an unversioned JackdeskChat.exe gave
 * no way to tell which build a workstation had, and caches served a stale one
 * under the same name. When you publish a build, change `url` and `filename`
 * together — `filename` is what the visitor's browser saves, and a mismatch is
 * how someone reports a bug against a version they are not running.
 *
 * `platform` decides who sees it: phones get the APKs, desktops get the .exe
 * files, and anything we cannot classify confidently sees everything rather
 * than nothing. An empty `url` hides the entry, so an unreleased platform can
 * be left blank rather than commented out.
 */
export type Platform = 'android' | 'windows' | 'macos' | 'linux' | 'any';

export interface Download {
  label: string;
  note: string;
  url: string;
  /** Suggested filename. Empty for guides, which open rather than download. */
  filename: string;
  /**
   * Announced but not yet downloadable. Renders as a labelled placeholder
   * instead of a link, so a build that is between uploads is visibly pending
   * rather than either a dead button or a silently missing product.
   */
  pending?: boolean;
  platform: Platform;
  /** install: the file downloads; doc: a guide, opened; store: an app store listing, opened (no filename). */
  kind: 'install' | 'doc' | 'store';
  /** Where it sits in the Downloads menu and page (owner, 2026-10-26: "downloads appears under their category"). */
  category: DownloadCategory;
}

export type DownloadCategory = 'phone' | 'desktop' | 'connector' | 'onprem';

/** The Downloads menu's columns, in order. The user manuals are a column of their own, from help.ts. */
export const DOWNLOAD_GROUPS: { id: DownloadCategory; label: string }[] = [
  { id: 'phone', label: 'Phone apps' },
  { id: 'desktop', label: 'Desktop apps' },
  { id: 'connector', label: 'Connectors' },
  { id: 'onprem', label: 'On-premise server' },
];

const S3 = 'https://s3.us-east-1.amazonaws.com/jackdesk.com/desktop-apps';

/** The phone app as published (owner, 2026-10-26): phones install it from Google Play, not a side-loaded APK. */
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.jackdesk.customer';

export const DOWNLOADS: Download[] = [
  {
    label: 'Customer Chat', category: 'phone',
    note: 'The omnichannel inbox on your phone, from Google Play.',
    url: PLAY_STORE_URL,
    filename: '',
    platform: 'android',
    kind: 'store',
  },
  {
    label: 'SMS Relay', category: 'phone',
    note: 'Sends campaign SMS through an Android handset on your own SIM.',
    // PENDING 2026-09-03. The APK was deliberately withdrawn from S3 and is
    // being rebuilt, so it shows as a placeholder rather than a 403. To
    // restore: put the path back and drop `pending`.
    //   url: `${S3}/sms-relay.apk`,
    url: '',
    pending: true,
    filename: 'JackdeskSmsRelay.apk',
    platform: 'android',
    kind: 'install',
  },
  {
    label: 'Team Chat', category: 'desktop',
    note: 'Desktop client for internal chat, calls and screen share.',
    url: `${S3}/JackdeskChatSetup-0.2.2.exe`,
    filename: 'JackdeskChatSetup-0.2.2.exe',
    platform: 'windows',
    kind: 'install',
  },
  {
    label: 'Workbook', category: 'desktop',
    note: 'Attendance, timesheets and field sessions, tracked from the desktop.',
    url: `${S3}/JackdeskSetup.exe`,
    // Saved name matches the object. It used to say JackWatchDog.exe while the
    // object was JackdeskSetup.exe — precisely the mismatch the note above
    // warns about, inherited from the Angular menu this list replaced.
    filename: 'JackdeskSetup.exe',
    platform: 'windows',
    kind: 'install',
  },
  {
    label: 'Backup & Sync', category: 'desktop',
    note: 'Keeps a folder in step across machines, with version history.',
    url: `${S3}/JackSyncSetup.exe`,
    filename: 'JackSync.exe',
    platform: 'windows',
    kind: 'install',
  },
  {
    label: 'Tally Connector', category: 'connector',
    note: 'Bridges Tally to Jackdesk. One install per Tally machine.',
    url: `${S3}/JackdeskTallyConnectorSetup.exe`,
    filename: 'JackdeskTallyConnectorSetup.exe',
    platform: 'windows',
    kind: 'install',
  },
  {
    label: 'On-Prem Server', category: 'onprem',
    note: 'WSL2, Postgres, Synapse and Caddy. Installed ONCE per firm, not per desk.',
    // 0.4.1 returned AccessDenied — it is not in the bucket. 0.4.0 is the
    // newest that actually resolves (checked 2026-09-03). Bump both lines
    // together once a newer build is published.
    url: `${S3}/JackdeskServerSetup-0.4.0.exe`,
    filename: 'JackdeskServerSetup-0.4.0.exe',
    platform: 'windows',
    kind: 'install',
  },
  {
    label: 'AI Assistant payload', category: 'onprem',
    note:
      'Only for a site with no internet: place it beside the server installer. ' +
      'The saved name must stay exactly jackdesk-ai.tar.gz — install-ai.sh looks ' +
      'for that literal name, so a browser appending " (1)" makes the installer ' +
      'silently download from dl.jackdesk.com instead.',
    url: `${S3}/jackdesk-ai.tar.gz`,
    filename: 'jackdesk-ai.tar.gz',
    platform: 'windows',
    kind: 'install',
  },
  // Deliberately only two of the four documents published to S3. The release
  // runbook and the installer-builds guide are INTERNAL — they describe the
  // build procedure and name the code-signing gap. They stay reachable by
  // direct URL for our own use, but this page is public with no auth guard in
  // front of it, so linking them here would publish them.
  {
    label: 'On-Prem setup guide', category: 'onprem',
    note: 'What to prepare before running the server installer.',
    url: `${S3}/jackdesk-onprem-setup.html`,
    filename: '',
    platform: 'any',
    kind: 'doc',
  },
  {
    label: 'Certificate guide', category: 'onprem',
    note: 'Issuing and renewing certificates for an on-prem box.',
    url: `${S3}/jackdesk-certificates.html`,
    filename: '',
    platform: 'any',
    kind: 'doc',
  },
];
